// src/locales/en.js — English guest text. The reference language: every key
// starts here, and fr-CA / es-MX fall back to it key by key.
//
// Grouped by screen. Plurals are objects ({ one, other }); slots are {name}.
// Read through tr() from src/i18n.js — never import this file directly.

const en = {
  // Shared by every guest list screen.
  common: {
    mainMenu: "Main Menu",
    starToSave: "☆ Star to save to My Menu",
    details: "Details ›",
    tapForDetails: "Tap for details ›",
    ask: "Ask",
    searchQuery: "\"{query}\"",
    glass: "glass",
    bottle: "bottle",
    addToMyMenu: "☆ Add to My Menu",
    addedToMyMenu: "★ Added to My Menu",
    added: "★ Added",
    // Stands in for the restaurant's name when it is not known. Used mid-sentence.
    thisRestaurant: "this restaurant",
  },

  // The Aa button in list headers.
  textSize: {
    normal: "Normal",
    large: "Large",
    larger: "Larger",
    title: "Text size: {size}",
    aria: "Text size: {size}. Tap for {next}.",
  },

  home: {
    pairingEyebrow: "Browse Food Menu",
    pairing: "Get a Wine Pairing",
    soon: "Soon",
    beverageMenu: "Beverage Menu",
    myMenu: "My Menu",
    howToUse: "How to Use This App",
    startOver: "Start Over",
    qrAlt: "QR code",
    scanWithPhone: "Scan with your phone",
    poweredBy: "Powered by SyncBev",
    privacy: "Privacy",
    // The language picker's label for screen readers. The choices themselves are
    // each written in their own language (GUEST_LANGUAGES in App.js), not translated.
    language: "Language",
    welcome: {
      eyebrow: "Welcome",
      heading: "I'm your sommelier for this evening.",
      browseTitle: "Browse",
      browseBody: "Tap any wine, beer, or cocktail for tasting notes, the label, and food that pairs with it.",
      askTitle: "Ask",
      askBody: "Tell us what you're eating tonight and we'll find a pairing.",
      saveTitle: "Save",
      saveBody: "Star anything you like — they gather in My Menu, ready to show your server or email to yourself before you go.",
      start: "Start Exploring",
    },
  },

  // Beer, Premium Pours, Cocktails and N/A share ItemListScreen.
  lists: {
    wine: { title: "Wine List" },
    beer: { title: "Beer List", all: "All Beers", search: "Search beer list…" },
    pours: { title: "Premium Pours", all: "All Premium Pours", search: "Search premium pours…" },
    cocktails: { title: "Specialty Cocktails", all: "All Specialty Cocktails", search: "Search specialty cocktails…" },
    nab: { title: "Non-Alcoholic Beverages", all: "All Beverages", search: "Search non-alcoholic beverages…" },
    // Used only if a list is added without its own all/search keys above.
    allFallback: "All {title}",
    searchFallback: "Search {list}…",
    loading: "Loading {title}...",
    unableToLoad: "Unable to load {title}",
    endpoint: "Endpoint: {endpoint}",
    error: "Error",
    loadFailed: "Unable to load menu",
    tryAgain: "Try Again",
    itemCount: { one: "{count} item", other: "{count} items" },
    each: "each",
    comingSoon: "Menu coming soon",
    noItems: "No items in this selection",
    defaultGroup: "Menu",
    abv: "{abv} ABV",
    backToList: "Back to the list",
  },

  wine: {
    colours: { red: "Red", white: "White", rose: "Rosé", sparkling: "Sparkling", dessert: "Dessert" },
    allWines: "All Wines",
    all: "All",
    byGlass: "By the Glass",
    byBottle: "By the Bottle",
    type: "Type",
    grape: "Grape",
    allGrapes: "All Grapes",
    moreGrapes: "+ {count} more",
    showFewer: "Show fewer",
    search: "Search wines, grapes, regions…",
    count: { one: "{count} wine", other: "{count} wines" },
    outOfStockCount: "{count} out of stock",
    noMatch: "No wines matching \"{query}\"",
    noneInSelection: "No wines in this selection",
    defaultGroup: "Wine",
    outOfStock: "Out of Stock",
    glassBottle: "glass/bottle",
    perGlass: "per glass",
    perBottle: "per bottle",
  },

  // The lines that rotate while an answer is on its way. LoadingMessages and the
  // chat's status line hold these keys in module constants and translate on show.
  loading: {
    sommelier: {
      cellar: "Your Virtual Sommelier is consulting the cellar…",
      swirling: "Swirling, sniffing, and considering your options…",
      evaluating: "Evaluating every bottle on the list…",
      tannins: "Checking tannins, acidity, and flavor bridges…",
      cellarMaster: "Conferring with the cellar master…",
      nose: "Nose: promising. Palate: almost there…",
      decanting: "Decanting the perfect recommendation…",
    },
    kitchen: {
      stepping: "Stepping into the kitchen…",
      chef: "Consulting with Chef on the perfect match…",
      weighing: "Weighing flavors, textures, and bridges…",
      plated: "Almost plated and ready…",
      terroir: "Matching the terroir to your table…",
    },
    chat: {
      thinking: "Thinking it over…",
      looking: "Looking through the list…",
      working: "Still working — a good pairing takes a moment",
      almost: "Almost there…",
    },
  },

  // SommelierChat and the modal shown when it closes.
  chat: {
    title: "Virtual Sommelier",
    done: "Done ✓",
    // The first message. It is also sent to the model as the assistant's opening
    // turn, so in another language the conversation starts in that language.
    openerFoods: "I can see you're considering {names}. Before I find wine pairings, is there anything I should know about your preferences — a style you love, something you'd like to avoid, or a budget in mind?",
    openerItem: "I see you're looking at the {name}. I can suggest a dish from tonight's menu to go with it, or find you something similar. What sounds good?",
    // Does not ask about dietary restrictions: the sommelier never vouches for them.
    opener: "Before I start my search, is there anything I should know about your preferences — a style you love, flavors you'd rather avoid, or a budget?",
    noReply: "I'm not sure — please ask your server.",
    // replyKey from the server when the wine list is empty mid-update.
    listUpdating: "Our list is being updated — please ask your server.",
    timedOut: "Sorry, that took longer than expected.",
    failed: "Something went wrong. Please ask your server for help.",
    tryAgain: "↻ Try again",
    // Saved on the item and shown in My Menu.
    pairedWith: "Paired with {dish}: {reason}",
    winePrompt: {
      title: "Add to My Menu?",
      body: "Would you like to save this wine to your menu before you go?",
      skip: "No thanks",
    },
    more: "more",
    less: "less",
    tapLabel: "Tap label to enlarge",
    add: "☆ Add",
    addFirst: "☆ 1ST",
    addMain: "☆ MAIN",
    hintSave: "Tap ☆ Add to My Menu to save, or keep chatting below.",
    // {done} is the bold Done ✓ (chat.done), placed by trRich().
    hintSaved: "Saved! Tap {done} when you're finished, or keep chatting.",
    placeholder: "Keep chatting…",
    doneModal: {
      title: "You're all set!",
      // {dishes} and {drinks} are the phrases below, each with its number in bold.
      body: "You've selected {dishes} and added {drinks} to your menu.",
      dishes: { one: "{n} dish", other: "{n} dishes" },
      wines: { one: "{n} wine", other: "{n} wines" },
      drinks: { one: "{n} drink", other: "{n} drinks" },
      review: "Ready to review your evening's selections?",
      viewMenu: "View My Menu →",
      keepBrowsing: "Keep Browsing",
    },
  },

  toast: {
    addedOne: "★ \"{name}\" added to My Menu",
    // Only ever shown for two or more.
    addedMany: "★ {count} items added to My Menu",
    view: "View →",
  },

  // SommelierScreen (Get a Wine Pairing) and the pairing buttons on drink details.
  pairing: {
    chatLine1: "✦ Chat with Our",
    chatLine2: "Virtual Sommelier",
    instantLine1: "Instant Pairing",
    instantLine2: "Suggestions",
    pairsWith: "Pairs beautifully with",
    noneFound: "Unable to find pairings — please ask your server.",
    title: "Wine Pairing",
    back: "Back",
    all: "All",
    instructions: "Select your dishes, then choose how you’d like to pair them",
    limit: "Choose up to 4 per course — for larger parties, run the sommelier a second time.",
    loadingMenu: "Loading menu…",
    // Column heads over the checkboxes, and the tag on a selected dish.
    column: { first: "1ST", main: "MAIN", dessert: "DES" },
    chip: { first: "1st", dessert: "Dessert" },
    howToPair: { one: "How would you like to pair this?", other: "How would you like to pair these?" },
    red: "Red",
    white: "White",
    rose: "Rosé",
    // The sparkling-wine part of the light button. Was "Bubbles" until Oct 29, 2026;
    // the key name stayed so App.js did not change.
    bubbles: "Sparkling",
    // The light-wine button names only the styles on the list.
    lightTwo: "{a} & {b}",
    lightThree: "{a}, {b} & {c}",
    pick: "Sommelier’s Pick",
    or: "or",
    ask: "✦ Ask the Sommelier",
    askSubtitle: "Tell me a wine you love, your price range, or that you’d like a cocktail instead, and I’ll find a match",
    selections: "Your table's selections",
    heading: {
      red: "Red pairing for",
      light: "{styles} pairing for",
      pick: "Sommelier’s Pick for",
      fallback: "Pairing for",
    },
    // Display only. The stored courseLabel on a wine stays English.
    course: { first: "First Course", main: "Main Course", dessert: "Dessert" },
    eachDish: "Or a wine for each dish",
    askLine1: "✦ Ask the",
    askLine2: "Sommelier",
    differentLine1: "Get Different",
    differentLine2: "Pairings",
    goToMyMenu: "★ Go to My Menu",
    repeatNote: "If suggestions repeat, it reflects the limits of our current wine selection for this dish.",
    startNew: "Start a New Pairing",
    priceGlass: "{price} glass",
    priceBottle: "{price} bottle",
    // Under the dish picker. The app makes no allergen or dietary claims.
    dietaryNote: "Dishes are shown for wine pairing only. For allergies or dietary needs, please ask your server.",
    // A pairing's tier. Display only: the stored level stays English.
    level: { value: "Value", mid: "Mid-Range", premium: "Premium" },
    // Server-chosen notes above the results (splitNoteKey / colourNoteKey from
    // getMatrixPairing). English must match the server's fallback text.
    note: {
      split: "These dishes call for different wines — here is one for each.",
      oneSuitsAll: "One wine on our list suits all of these dishes.",
      oneSuitsAllByDish: "One wine on our list suits all of these dishes. Below it, a wine for each dish on its own.",
      theseSuitAll: "These are the wines that suit all of these dishes. Ask for different pairings for a wine matched to each dish.",
      // {noun} is one of pairing.note.noun.*, article included.
      colourStretch: {
        red: "Red is a stretch with this. Your closest red picks are below, but if you're open to {noun}, this would be a much better match:",
        light: "A lighter wine is a stretch with this. Your closest white, rosé and sparkling picks are below, but if you're open to {noun}, this would be a much better match:",
      },
      colourBetter: {
        red: "Your red picks are below. If you're open to {noun}, this would be an even better match:",
        light: "Your white, rosé and sparkling picks are below. If you're open to {noun}, this would be an even better match:",
      },
      noun: { red: "a red", white: "a white", rose: "a rosé", sparkling: "a sparkling wine", dessert: "a dessert wine", other: "something different" },
    },
    // Shown in place of results (messageKey from getMatrixPairing).
    msg: {
      vague: "We need a bit more detail about this dish before recommending a wine — please ask your server.",
      colourEmptyRed: "We do not have a red available for this right now. Try Sommelier's Pick, or ask your server.",
      colourEmptyLight: "We do not have a white, rosé or sparkling wine available for this right now. Try Sommelier's Pick, or ask your server.",
      nothingElse: "Nothing else on the list is a strong match for this combination.",
      shownAll: {
        red: "We have shown you every red that suits this combination.",
        light: "We have shown you every white, rosé and sparkling wine that suits this combination.",
        pick: "We have shown you everything that suits this combination.",
      },
    },
  },

  // ShortlistScreen (My Menu on the tablet). Its course headings are
  // pairing.course.*, translated where shown; the stored courseLabel stays English.
  myMenu: {
    title: "My Menu",
    subtitle: "Your evening's selections",
    back: "‹ Back",
    readyToOrder: "Ready to order? Your server will be happy to help.",
    empty: "Star wines, dishes, and drinks to build your menu for the evening",
    wines: "Wines",
    drinks: "Drinks",
    // Under the dishes in My Menu and on the saved-menu page.
    dietaryNote: "For allergies or dietary needs, please ask your server.",
    // The line under a starred drink. A beer shows its own style instead when it has one.
    kind: { beer: "Beer", cocktail: "Cocktail", nab: "Non-Alcoholic", pour: "Premium Pour" },
    // Under "Start a New Pairing" (pairing.startNew).
    newPairingHint: "Dessert, another course, or a friend’s dish",
    email: {
      title: "Take the evening with you",
      body: "We'll email your picks — what you chose, why it worked, and a link to view them anytime.",
      placeholder: "your@email.com",
      hint: "Sharing with your table? Separate addresses with commas — everyone gets their own copy.",
      // The mailing-list checkbox. Also saved with the guest's consent as the
      // exact wording they agreed to, so it is recorded in the language shown.
      consent: "Also send me news and events from {venue}",
      send: "Send it to me",
      sending: "Sending…",
      failed: "That didn't go through — check the address and try again.",
      badAddress: "\"{address}\" doesn't look like an email address.",
      tooMany: "Up to {max} addresses at a time.",
      // Some addresses sent, some did not. {failed} is the list that failed.
      partial: "Sent to {sent}. Couldn't reach {failed} — check and try again.",
      // Only ever shown for two or more.
      sentMany: "Sent to {count} guests",
      sentManyBody: "Everyone gets their own copy.",
      sentOne: "Sent — check your inbox",
      sentOneBody: "Your picks are on their way.",
      sentOneBodyList: "Your picks are on their way, and you're on the list.",
      privacyLink: "How we use your email address",
    },
  },

  // The saved-menu page a guest opens from their email (GuestMenuLoader /
  // GuestMenuScreen). Its title, sections and buttons reuse myMenu.* and common.*.
  guestMenu: {
    loading: "Loading your menu…",
    expiredTitle: "This menu link has expired",
    expiredBody: "Menu QR codes are valid for 24 hours. We hope your evening was wonderful — we'd love to welcome you back soon.",
    notFound: "Menu not found. Please ask your server for assistance.",
    logoAlt: "Restaurant logo",
    welcomeBack: "We'd love to welcome you back to {venue}.",
    // {site} is the restaurant's web address without https:// ("gildedfork.com").
    visit: "Visit {site}",
  },

  // A wine label shown large, on the tablet (LabelModal) and the saved-menu page.
  labelZoom: {
    aria: "{name} label",
    tapToClose: "Tap anywhere to close",
    // The tablet's version is written in capitals rather than styled, so it has its own key.
    tapToCloseCaps: "TAP ANYWHERE TO CLOSE",
    // The two standard disclaimers stored on items by the server. English must
    // match LABEL_DISCLAIMER / GENERIC_DISCLAIMER in functions/index.js exactly:
    // disclaimerText() in App.js matches on it.
    disclaimerWine: "Label shown for reference — the actual label may differ by vintage. Ask your server to see the bottle.",
    disclaimerOther: "Label shown for reference — actual packaging may vary.",
    // Under the enlarged label: a guest flags a wrong image. reportThanks replaces it once sent.
    reportImage: "Report incorrect image",
    reportThanks: "Thank you — we’ll check this image.",
  },

  // WineDetailPanel. Its pairing buttons and results reuse pairing.* and common.*.
  wineDetail: {
    // Under a stock image that may not be this vintage's label.
    representativeLabel: "Representative label",
    askTastingNotes: "Ask your server for tasting notes",
    reviews: "Reviews & Ratings",
    glass: "Glass",
    bottle: "Bottle",
    askPricing: "Ask your server for pricing",
  },

  // PrivacyNotice - opened from My Menu and the home screen. It is a legal notice:
  // a translation should be checked against GUEST-PRIVACY-NOTICE.md, not just for tone.
  // {name} is the restaurant, or common.thisRestaurant.
  privacy: {
    title: "About this app and your information",
    intro: "This app is provided by SyncBev on behalf of {name}. Here is what happens with your information.",
    usingHeading: "Using the app",
    usingBody: "You can browse the menu, ask for food pairings, and talk to the virtual sommelier without giving us anything. We do not require you to sign in, and we do not know who you are. Nothing you do here is connected to you unless you choose to give us your email address, as explained below.",
    aiHeading: "The sommelier and pairings",
    // {notTrained} is aiNotTrained, in bold.
    aiBody: "What you type to the virtual sommelier, and the dishes you pick for a pairing, are sent to an AI service so it can write you an answer. Under our agreement with that provider, they are used only to answer you and are {notTrained}.",
    aiNotTrained: "not used to train AI models",
    aiAdvice: "The sommelier is there to talk about food and drink. There is no need to tell it anything about yourself, and we would rather you did not.",
    picksHeading: "Your saved picks",
    picksDevice: "Items you star are kept on this device while you browse.",
    picksEmailed: "If you email yourself your picks, that list is stored so the link in the email keeps working. It does not expire, so you can open it again later.",
    emailHeading: "Your email address",
    emailNeverRequired: "You never have to give one to use the app. If you choose to email yourself your picks:",
    // Bold, at the start of emailWithList / emailNoList ({once}).
    emailOnce: "We use your address to send you that one email, and then we do not keep it.",
    // Where the restaurant offers its mailing-list checkbox.
    emailWithList: "{once} SyncBev does not store, share, sell, or market to your address — unless you check the box instructing us to give it to {name} for their mailing list. If you do:",
    listMayEmail: "{name} may then email you about the restaurant, and those emails come from them",
    listUnsubscribe: "To stop receiving them, use the unsubscribe link in their email or contact the restaurant directly",
    listNotOurs: "SyncBev does not use your address for anything of our own",
    // Where it does not.
    emailNoList: "{once} SyncBev does not store, share, sell, or market to your address, and does not give it to anyone.",
    removeHeading: "Removing your email address",
    removeWithList: "To have it removed from {name}'s records, contact the restaurant directly. To have SyncBev delete our copy, write to privacy@syncbev.com.",
    removeNoList: "We do not keep it, so there is nothing to remove. For anything else, write to privacy@syncbev.com.",
    version: "Version {version}",
  },

  // Our default room names, shown when the restaurant kept them. A room the owner
  // named ("Tuques") is the restaurant's own and is never translated - see
  // roomName() in App.js.
  rooms: { bar: "Bar", dining: "Dining Room" },

  // The bar across the top of every guest screen when the tablet loses its connection.
  // Shown instead of the menu when the restaurant's account is switched off.
  unavailable: {
    title: "This menu isn’t available",
    body: "The digital menu for this restaurant isn’t available right now. Please ask a member of staff for a menu.",
  },

  connection: {
    title: "No internet connection",
    kiosk: "This tablet can’t load the menu right now. Please let a member of staff know.",
    phone: "The menu can’t load right now. Check your signal — it will reconnect by itself.",
  },

  // Banners on demo restaurants. privateBanner is on a prospect's private demo;
  // the trial line is on the public Gilded Fork demo, phones and tablets only.
  demo: {
    // DemoWallCard, the public demo's free-preview limit. The HubSpot scheduler and
    // form inside it are HubSpot's own pages, in whatever language HubSpot is set to.
    wall: {
      trialTitle: "Sign up for your 3-month free trial",
      trialBody: "Try SommSync at your own restaurant. Upload your menus in any format and we can build your app within 24 hours.",
      callTitle: "Book a sales call",
      callBody: "A 20-minute call to show you how SommSync can work in your restaurant and amplify your beverage program and menu.",
      callFrame: "Schedule a SommSync walkthrough",
      formTitle: "Send us your details",
      formBody: "Fill out the contact form and we'll reach out to you within 24 hours.",
      backInMoment: "Back in a moment",
      limitReached: "Free preview limit reached",
      busy: "This demo is getting a lot of attention right now — please try again shortly.",
      keepBrowsing: "Keep browsing",
      backToOptions: "‹ Back to options",
      loading: "Loading…",
      intro: "Put SommSync to work on your own menu. Choose how you'd like to get started.",
      noThanks: "No thanks, just keep browsing",
    },
    // The public demo's colour-theme switcher (DemoThemeSwitcher). Theme names are
    // keyed by THEME_CATALOGUE value; a theme added there without a name here shows
    // its catalogue label.
    theme: {
      pill: "Select a color theme for your demo",
      title: "Demo · Color Themes",
      count: "{current} of {total}",
      applied: "✓ Applied",
      current: "This is the current theme",
      apply: "Apply {name}",
      usingDefault: "Using the default theme",
      reset: "Reset to default",
      dismissAria: "Dismiss color theme picker",
      dismissTip: "Dismiss — won't show again this session",
      collapseAria: "Collapse back to pill",
      collapseTip: "Collapse — you can reopen this anytime",
      names: {
        oliveAndLinen: "Olive & Linen",
        emeraldAndCream: "Emerald & Platinum",
        parchmentAndMaple: "Parchment & Maple",
        espressoAndBrass: "Espresso & Brass",
        inkAndPaper: "Ink & Paper",
        onyxAndChalk: "Onyx & Chalk",
        cobaltAndWhite: "Cobalt & White",
        slateAndChrome: "Navy & White",
        terracottaAndChalk: "Terracotta & Chalk",
        blackAndCherry: "Black & Cherry",
      },
    },
    privateBanner: "DEMO — built only from menus published online, so prices and some wine labels are missing. Connected to your POS, this fills in automatically and stays in sync.",
    trialBanner: "You're exploring a demo restaurant.",
    startTrial: "Start Free Trial ›",
    hide: "Hide this message",
  },
};

export default en;
